import { useTranslation } from "react-i18next";
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import styled from "@emotion/styled";
import { INotification } from "interfaces/Notifications";
import PermIdentityIcon from "@mui/icons-material/PermIdentity";
import Profile from "assets/Profile_pic.png";
import i18n from "i18n";
import { BootstrapTooltip } from "components/PWATabs/mainTab/CommentsTable";
import DeleteIcon from "@mui/icons-material/Delete";

interface IIndividualNotificationsTabProps {
  individualNotifications: INotification[];
  deleteIndividualNotification: (id: string) => void;
}

type Locale = "ua" | "ru" | "en";

const TypographyStyled = styled(Typography)(() => {
  return {
    whiteSpace: "nowrap",
    fontSize: "14px",
    fontWeight: 500,
    fontFamily: "var(--cygreRegular), sans-serif",
    color: "#6D6777",
  };
});

const IndividualNotificationsTab = ({
  individualNotifications,
  deleteIndividualNotification,
}: IIndividualNotificationsTabProps) => {
  const { t } = useTranslation();
  const currentLanguage = (i18n.language as Locale) ?? "en";

  return (
    <>
      <Typography
        sx={{
          fontFamily: "var(--cygreMedium), sans-serif",
          color: "#2E263D",
          fontSize: "16px",
          lineHeight: 1.22,
          textAlign: "center",
          marginBottom: "24px",
        }}
      >
        {t("individualNotificationsList")}
      </Typography>
      <TableContainer sx={{ maxHeight: "460px", overflowY: "auto", marginBottom: "40px" }}>
        <Table stickyHeader aria-label="individual notifications table">
          <TableHead sx={{ zIndex: 1 }}>
            <TableRow sx={{ backgroundColor: "#F6F7FB" }}>
              <TableCell
                sx={{
                  paddingLeft: "36px",
                  borderTopLeftRadius: "8px",
                  borderBottomLeftRadius: "8px",
                }}
              >
                <TypographyStyled>{t("user")}</TypographyStyled>
              </TableCell>
              <TableCell>
                <TypographyStyled>{t("notificationText")}</TypographyStyled>
              </TableCell>
              <TableCell
                sx={{
                  borderTopRightRadius: "8px",
                  borderBottomRightRadius: "8px",
                }}
              >
                <TypographyStyled
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  {t("actions")}
                </TypographyStyled>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {individualNotifications.map(({ _id, userId, userName, text }) => (
              <TableRow key={_id}>
                <TableCell
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    paddingLeft: "36px",
                  }}
                >
                  <img
                    src={Profile}
                    alt="icon"
                    style={{
                      width: 40,
                      height: 40,
                      marginRight: "18px",
                      borderRadius: "50%",
                    }}
                  />
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 400,
                        fontFamily: "var(--cygreRegular), sans-serif",
                      }}
                    >
                      {userName}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <PermIdentityIcon
                        sx={{
                          color: "#6D6777",
                          width: "18px",
                          height: "18px",
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: "10px",
                          fontWeight: 400,
                          fontFamily: "var(--cygreRegular), sans-serif",
                          color: "#6D6777",
                        }}
                      >
                        {userId}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>

                <TableCell sx={{ minWidth: "260px", borderBottom: "none" }}>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 400,
                      fontFamily: "var(--cygreRegular), sans-serif",
                      display: "-webkit-box",
                      WebkitBoxOrient: "vertical",
                      WebkitLineClamp: 2,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {text[currentLanguage]}
                  </Typography>
                </TableCell>

                <TableCell>
                  <IconButton
                    sx={{ display: "block", margin: "0 auto" }}
                    aria-label="delete-icon"
                    onClick={() => deleteIndividualNotification(_id)}
                  >
                    <BootstrapTooltip title={t("delete")} placement="top">
                      <DeleteIcon sx={{ width: "18px", height: "18px", color: "#6D6777" }} />
                    </BootstrapTooltip>
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default IndividualNotificationsTab;
